import React, { useCallback, useEffect, useRef, useState } from "react";
import Loadable from "react-loadable";

import { Channel, Topic } from "../../../constants";
import { MessageBrokerUtils, PageContextUtils } from "../../../utils";
import { EmptyComponent } from "../EmptyComponent";
import { ToastActionType, ToastType } from "./constants";
import { Theme } from "../../../constants";
import { IgnoreClickOutsideAttribute } from "../../../constants";

const ToastRootContainer = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "toast/root" */
      "./ToastRootContainer"
    ),
  render(loaded, props) {
    let Component = loaded.ToastRootContainer;
    return <Component {...props} />;
  },
  loading: EmptyComponent
});

const computeToastTemplateComponent = templateName => {
  return Loadable({
    loader: () =>
      import(
        /* webpackChunkName: "toast/toast.[request]" */
        `./Templates/${templateName}`
      ),
    loading: EmptyComponent
  });
};

const importToastCss = () => {
  import(/* webpackChunkName: "toast/styles" */ "./CSS/toast.scss");
};

const importReactToastify = async () => {
  const reactToastify = await import(
    /* webpackChunkName: "toast/react-toastify" */ "react-toastify"
  );

  return reactToastify;
};

const Toast = () => {
  const reactToastifyToastRef = useRef(null);
  const toastRef = useRef(null);

  const [ToastContainerData, setToastContainerData] = useState({
    ToastContainer: null
  });

  const [isToastContainerDataLoaded, setToastContainerDataLoaded] =
    useState(false);

  const computeToastTemplateName = useCallback(options => {
    return options?.template;
  }, []);

  const computeToastTemplateOptions = useCallback((templateName, options) => {
    let toastOptions = { autoClose: 7000, closeOnClick: false };

    switch (templateName) {
      case ToastType.DefaultUC:
      case ToastType.RegulationLogin:
        Object.assign(toastOptions, options);
        break;
      default:
        toastOptions = Object.assign({}, options);
    }

    return toastOptions;
  }, []);

  const showToast = useCallback((templateName, options, data) => {
    switch (templateName) {
      case ToastType.DefaultUC:
      case ToastType.RegulationLogin:
        {
          const ToastTemplateComponent =
            computeToastTemplateComponent(templateName);

          const ToastTemplate = () => {
            return <ToastTemplateComponent data={data} />;
          };

          toastRef.current = reactToastifyToastRef.current(
            <ToastTemplate />,
            options
          );
        }
        break;
      default:
        reactToastifyToastRef.current(data.message, options);
    }
  }, []);

  const destroyToast = useCallback(() => {
    reactToastifyToastRef.current.dismiss();
  }, []);

  const setToastPrerequisites = async () => {
    importToastCss();

    const { toast } = await importReactToastify();

    reactToastifyToastRef.current = toast;
  };

  const processToast = useCallback(
    async ({ actionType, options, data }) => {
      if (!isToastContainerDataLoaded) {
        await setToastPrerequisites();

        setToastContainerDataLoaded(true);
      }

      if (actionType === ToastActionType.Destroy) destroyToast();

      if (actionType === ToastActionType.Dismiss)
        toastRef.current.dismiss(options.toastId);

      if (actionType === ToastActionType.Show) {
        const templateName = computeToastTemplateName(options);
        const templateOptions = computeToastTemplateOptions(
          templateName,
          options
        );

        showToast(templateName, templateOptions, data);
      }
    },
    [
      isToastContainerDataLoaded,
      computeToastTemplateName,
      computeToastTemplateOptions,
      showToast,
      destroyToast
    ]
  );

  useEffect(() => {
    if (isToastContainerDataLoaded)
      setToastContainerData({ ToastContainer: ToastRootContainer });
  }, [isToastContainerDataLoaded]);

  useEffect(() => {
    const toastSubscription = MessageBrokerUtils.subscribe({
      channel: Channel.UI,
      topic: Topic.Toast,
      callback: processToast
    });

    return () => {
      toastSubscription?.unsubscribe();
    };
  }, [processToast]);

  const { ToastContainer } = ToastContainerData;

  if (!ToastContainer) return null;

  const { currentName = Theme.Default } = PageContextUtils.getThemeData();

  const additionalProps = { [IgnoreClickOutsideAttribute]: "" };

  return (
    <div {...additionalProps}>
      <ToastContainer
        options={{ theme: currentName === Theme.MrGreen ? "light" : "dark" }}
      />
    </div>
  );
};

export default React.memo(Toast);
